import dataUtils from '@/common/dataUtils';
import MODULE_ID from './moduleId';
import moduleIndex from './index';
import begleitfahrtApi from './begleitfahrtApi';
import { cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { SYNC_STATES } from '@/store/sync/sync';
import moduleId from '@/modules/fahrplan/moduleId';
import i18n from '@/plugins/i18n';

const ROOT = { root: true };

const state = {
  additionalFieldById: {},
  additionalFieldResultById: {},
  answerSetsById: {},
  answerById: {},
  deviceUserBoss: [],
  questionnaireById: {},
  questionnaireAnswerDescription: [],
  questionnaireQuestionById: {},
  questionnaireQuestionResultById: {},
  questionnaireTopicById: {},
  rating: [],
  ratingById: {},
  reportAboById: {},
  reportMedia: [],
  period: [],
  config: {},
  users: [],
  userById: {},
  unreadCount: 0, // or null ?
  questionnaireLocalData: {},
  questionnaireLocalDataOffline: {},
  ratingsByUserId: {},
  prefillDataByUserId: {},
  dataLoaded: false,
};

const getters = {
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    // TODO: Implement unread counter
    return state.unreadCount;
  },
};

const mutations = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {},
  setData(state, data) {
    //storeService.saveDataToInMemoryStore(MODULE_ID, state, IN_MEMORY_DB, data);
    Object.keys(data).forEach((key) => (state[key] = data[key]));
    // state.additionalFieldById = data.additionalFieldById;
    // state.additionalFieldResultById = data.additionalFieldResultById;
    // state.answerSetsById = data.answerSetsById;
    // state.answerById = data.answerById;
    // state.deviceUserBoss = data.deviceUserBoss;
    // state.questionnaireAnswerDescription = data.questionnaireAnswerDescription;
    // state.questionnaire = data.questionnaire;
    // state.questionnaireById = data.questionnaireById;
    // state.questionnaireQuestionById = data.questionnaireQuestionById;
    // state.questionnaireTopicById = data.questionnaireTopicById;
    // state.questionnaireQuestionResultById = data.questionnaireQuestionResultById;
    // state.questionnaireQuestionResultsByRatingId = data.questionnaireQuestionResultsByRatingId;
    // state.rating = data.rating;
    // state.ratingByUserId = data.ratingByUserId;
    // state.ratingById = data.ratingById;
    // state.ratingsByUserId=data.ratingsByUserId;
    // state.reportAboById = data.reportAboById;
    // state.reportMedia = data.reportMedia;
    // state.config = data.config;
    // state.period = data.period;
    // state.userById = data.userById;
    //console.debug({ questionnaireLocalData: state.questionnaireLocalData });
    const existingQuestionnaireLocalData = state.questionnaireLocalData;
    if (Object.keys(existingQuestionnaireLocalData).length === 0) {
      /**
       * We only use the questionnaire-local-data from the local storage
       * if no data in in the vuex store exists
       * Consideration: 'Master' is the vuex store.
       */
      state.questionnaireLocalData = data.questionnaireLocalData; // initial
    }
    if (!(Object.keys(state.questionnaireLocalDataOffline).length === 0) && navigator.onLine) {
      state.questionnaireLocalDataOffline = {};
    }
    state.dataLoaded = true;
  },
  deleteQuestionnaireData(state, { userId, uuid, markedAsDeleted }) {
    if (state.questionnaireLocalData[userId] && state.questionnaireLocalData[userId][uuid]) {
      // Check if user is offline and Begleitfahrt was not marked as deleted
      if (!navigator.onLine && !markedAsDeleted) {
        // Initialize the nested structure if not already present
        if (!state.questionnaireLocalDataOffline[userId]) {
          state.questionnaireLocalDataOffline[userId] = {};
        }
        // Copy the data
        state.questionnaireLocalDataOffline[userId][uuid] = state.questionnaireLocalData[userId][uuid];
      }
      delete state.questionnaireLocalData[userId][uuid];
      // Delete the user if no UUIDs left
      if (Object.keys(state.questionnaireLocalData[userId]).length === 0) {
        delete state.questionnaireLocalData[userId];
      }
    }
  },
  setQuestionnaireLocalData(state, data) {
    /** Got all properties -> save */
    if (!state.questionnaireLocalData[data.userId]) {
      state.questionnaireLocalData[data.userId] = {};
    }
    state.questionnaireLocalData[data.userId][data.uuid] = data.questionnaireLocalData;
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }) {
    return performInMemorySearch({
      MODULE_ID,
      items: state.questionnaire,
      titleSearchAttr: 'title',
      contentSearchAttr: null,
      // TODO maybe add more later on?
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    // empty function
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    // TODO reportMedia
    const reportMediaFiles = state.reportMedia.map((media) => {
      return { filename: media.filename, checksum: media.checksum };
    });

    await cacheAllModuleFiles({ files: reportMediaFiles, moduleIndex: moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, state, dispatch, rootState, rootGetters }) {
    try {
      const response = await begleitfahrtApi.getData();
      const {
        additionalField,
        additionalFieldResult,
        answer,
        answerSet,
        config,
        deviceUserBoss,
        period,
        questionnaire,
        questionnaireAnswerDescription,
        questionnaireQuestion,
        questionnaireQuestionResult,
        questionnaireSection,
        questionnaireTopic,
        rating,
        reportAbo,
        reportMedia,
        users,
        prefillData,
      } = response.data;
      let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({ moduleType: moduleIndex.moduleType, instance: 1 });

      // TODO: Create search information for begleitfahrt rating instances - if needed
      // questionnaire.forEach(q=>{
      //   q._displayLocation = '';
      //   q._moduleTitle = moduleConfig.title;
      //   q._path = `/begleitfahrt/1/${q.id}`; //TODO: Create route for opening duty directly
      //   q._moduleId = MODULE_ID;
      //
      // })
      const ratingsByUserId = dataUtils.buildArrayMapById(rating, (item) => item.userIdFk);
      //console.debug('ratingsByUserId', ratingsByUserId);
      // Create maps by ID
      const additionalFieldById = dataUtils.buildIdMap(additionalField, 'id'); //TODO: Not needed, replace by additionalField
      const additionalFieldResultById = dataUtils.buildIdMap(additionalFieldResult, 'id');
      const answerSetsById = dataUtils.buildIdMap(answerSet, 'id');
      const answerById = dataUtils.buildIdMap(answer, 'id');
      const questionnaireById = dataUtils.buildIdMap(questionnaire, 'id');
      const questionnaireQuestionResultById = dataUtils.buildIdMap(questionnaireQuestionResult, 'id');
      //const questionnaireAnswerDescriptionById = dataUtils.buildIdMap(questionnaireAnswerDescription, 'id')
      const questionnaireQuestionById = dataUtils.buildIdMap(questionnaireQuestion, 'id');
      //const questionnaireSectionById = dataUtils.buildIdMap(questionnaireSection, 'id')
      const questionnaireTopicById = dataUtils.buildIdMap(questionnaireTopic, 'id');
      const ratingById = dataUtils.buildIdMap(rating, 'id');
      const reportAboById = dataUtils.buildIdMap(reportAbo, 'id');
      //const reportMediaById = dataUtils.buildIdMap(reportMedia, 'id')

      const userById = dataUtils.buildIdMap(users, 'id');

      const prefillDataByUserId = dataUtils.buildIdMap(prefillData, 'id');

      for (const [id, data] of Object.entries(prefillDataByUserId)) {
        const bosses = [];
        for (const relation of deviceUserBoss) {
          if (relation.userId == id) {
            if (userById[relation.bossId] && !userById[relation.bossId].deleted) {
              bosses.push(String(userById[relation.bossId].firstname + ' ' + userById[relation.bossId].lastname).trim());
            }
          }
        }
        bosses.sort();
        prefillDataByUserId[id]['boss'] = bosses;
      }

      // Initialize answers property if not existent and create a mapping for quick look up
      const answerIdToAnswerMap = {};
      answer.forEach((a) => {
        if (answerSetsById[a.answerSetIdFk]) {
          if (!answerSetsById[a.answerSetIdFk].answers) {
            answerSetsById[a.answerSetIdFk].answers = [];
          }
          answerSetsById[a.answerSetIdFk].answers.push(a);
          answerIdToAnswerMap[a.id] = a;
        }
      });
      //console.debug('answerSetsById', answerSetsById);

      // Use the created map for direct access
      questionnaireAnswerDescription.forEach((desc) => {
        let answer = answerIdToAnswerMap[desc.answerIdFk];
        if (answer) {
          answer.description = desc.description;
        }
      });

      // Prepare questionnaire
      const sectionById = {};
      questionnaireSection.forEach((section) => {
        sectionById[section.id] = section;
        section.topics = [];
      });

      // Associate sections with their respective questionnaires
      questionnaireSection.forEach((section) => {
        const questionnaire = questionnaireById[section.questionnaireIdFk];
        if (questionnaire) {
          if (!questionnaire.sections) {
            questionnaire.sections = [];
          }
          questionnaire.sections.push(section);
        }
      });
      questionnaire.forEach((q) => {
        if (q.sections) {
          q.sections.sort((a, b) => a.sort - b.sort);
        }
      });
      // Object.values(questionnaireById).forEach((questionnaire) => {
      //   if (questionnaire.sections) {
      //     questionnaire.sections.sort((a, b) => a.sort - b.sort);
      //   }
      // });

      // Nest topics within the correct section and sort topics
      questionnaireTopic.forEach((topic) => {
        const section = sectionById[topic.sectionIdFk];
        if (section) {
          section.topics.push(topic);
        }
      });
      Object.values(sectionById).forEach((section) => {
        if (section.topics) {
          section.topics.sort((a, b) => a.sort - b.sort);
        }
      });

      const questionnaireQuestionResultsByRatingId = {};
      questionnaireQuestionResult.forEach((result) => {
        if (!questionnaireQuestionResultsByRatingId[result.ratingIdFk]) {
          questionnaireQuestionResultsByRatingId[result.ratingIdFk] = [];
        }
        questionnaireQuestionResultsByRatingId[result.ratingIdFk].push(result);
      });

      /**
       * Get questionnaire-data from local storage if exists
       * Info: The mutation will only keep this data if 'questionnaireLocalData' data does not already exist in the vuex store
        Consideration: 'Master' is the vuex store.
       */
      let questionnaireLocalData = localStorage.getItem('questionnaireLocalData');
      if (questionnaireLocalData === null) {
        questionnaireLocalData = {};
      } else {
        questionnaireLocalData = JSON.parse(questionnaireLocalData);
      }

      commit('setData', {
        additionalFieldById,
        additionalFieldResultById,
        answerSetsById,
        answerById,
        deviceUserBoss,
        questionnaire,
        questionnaireById,
        questionnaireAnswerDescription,
        questionnaireQuestionById,
        questionnaireTopicById,
        questionnaireQuestionResultById,
        questionnaireQuestionResultsByRatingId,
        questionnaireLocalData,
        rating,
        ratingById,
        ratingsByUserId,
        reportAboById,
        reportMedia,
        period,
        config,
        users,
        userById,
        prefillDataByUserId,
      });
    } catch (error) {
      console.error(error);
      commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.FAILURE, msg: error }, ROOT);
      throw error;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
  deleteQuestionnaireDataInStateAndStorage({ commit, state }, { userId, uuid, markedAsDeleted = false }) {
    commit('deleteQuestionnaireData', { userId, uuid, markedAsDeleted });
    localStorage.setItem('questionnaireLocalData', JSON.stringify(state.questionnaireLocalData));
  },

  async saveQuestionnaireLocalData({ commit, state, dispatch, rootState, rootGetters }, data) {
    if (!data.userId || !data.uuid || !data.questionnaireLocalData) {
      // TODO Micha: handle error by throwing error. It is not useful to log an error only
      // Missing one of the needed properties -> error
      console.error({
        a1: !data.userId,
        b2: !data.uuid,
        c3: !data.questionnaireLocalData,
        ...data,
        message: 'Can not persist questionnaireLocalData',
      });
      return;
    }

    commit('setQuestionnaireLocalData', data);

    // TODO Micha: /** */ comments are reseved for function and class comments. Replace by // comments
    /*
     * Save a copy on local storage
     * Consideration: 'Master' is the vuex store.
     */
    localStorage.setItem('questionnaireLocalData', JSON.stringify(state.questionnaireLocalData));
  },
  async storeBegleitfahrt({ commit, dispatch, rootState, rootGetters }, { questionnaireData, questionnaireId, userId, teamleaderId, uuid }) {
    let action = 'SubmittedAction';
    let subject = 'begleitfahrt';
    let moduleId = 'begleitfahrt';
    let payload = { questionnaireData, questionnaireId, userId, teamleaderId, uuid };
    let userState = {};
    let title = 'Begleitfahrt';
    let text = 'Begleitfahrt Bewertung übermittelt';
    let showAsUserInfo = false;
    let showInSendbox = true;
    let writeAuditLog = false;

    await dispatch(
      'sendbox/performSendboxItem2Action',
      { moduleId, subject, payload, userState, title, text, action, showAsUserInfo, showInSendbox, writeAuditLog },
      { root: true }
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
